import type { FilterFunc } from '@tunasong/models'
import { deepEqual, isStoredObject, logger, shortUuid } from '@tunasong/models'
import { isValidSlateDocument } from '@tunasong/models/element/valid-document'
import type { CoreElement, Entity, Persisted } from '@tunasong/schemas'
import { isCoreElement, isEntityLink, isEntityRef, isYDocStorage } from '@tunasong/schemas'
import { Editor, Transforms, createEditor } from 'slate'
import invariant from 'tiny-invariant'
import { S3Doc } from '../storage/doc-s3.js'
import { createDocStore } from '../storage/doc-store.js'
import { expandYDocV2 } from './doc-utils.js'

/**
 * Document reads go through the seam: S3 in the cloud, the local filesystem on a local
 * host. This class is reached from the edge-maintenance tasks, which run off the outbox
 * locally, so a direct S3 read here sent every locally-typed document to AWS.
 */
const docStore = createDocStore()

/**
 * Handle an editable entity node, i.e., a Slate Document or a CoreElement
 */
export class EditableEntityNode<T extends Persisted<Entity> = Persisted<Entity>> {
  /** Entity. For CollabEntities the yDoc value is unwrapped into children  */
  #editor: Editor | null = null
  #element: T | null = null

  constructor(
    private rawEntity: T,
    /**
     * @deprecated The bucket is resolved by the registered DocStore now. The parameter is
     * kept because it is threaded through every task-params and server-event generator
     * signature in graph-events; unpicking that thread is its own change.
     */
    private _bucketName: string
  ) {}

  /** Diff of the Slate Document for the node (if any) */
  static diffElements = async <T extends CoreElement>(
    a: EditableEntityNode | null,
    b: EditableEntityNode | null,
    match: FilterFunc
  ) => {
    const oldElements = (await a?.findElements<T>(match)) ?? []
    const newElements = (await b?.findElements<T>(match)) ?? []

    const added: T[] = []
    const removed: T[] = []

    /**
     * @note this will NOT detect when we add an element that is equal to another element
     *
     * Specifically, if we have two mentions with the same userIds, this will not detect that we added a mention
     */

    const countDeepEqual = (arr: CoreElement[], e: CoreElement) => arr.filter(n => deepEqual(e, n)).length

    for (const e of oldElements) {
      if (!newElements.some(n => deepEqual(e, n))) {
        removed.push(e)
      }
    }
    for (const e of newElements) {
      if (countDeepEqual(oldElements, e) < countDeepEqual(newElements, e) && !added.find(n => deepEqual(e, n))) {
        added.push(e)
      }
    }

    return { added, removed }
  }

  getElement = async () => {
    if (!this.#element) {
      await this.load()
    }
    invariant(this.#element, 'Element should be loaded')
    return this.#element
  }

  getEditor = async () => {
    if (!this.#editor) {
      await this.load()
    }
    invariant(this.#editor, 'Editor should be loaded')
    return this.#editor
  }

  findElements = async <S extends CoreElement>(match: FilterFunc) => {
    const editor = await this.getEditor()
    const at = {
      anchor: Editor.start(editor, []),
      focus: Editor.end(editor, []),
    }
    const nodes = Editor.nodes(editor, { match, mode: 'all', voids: true, at })

    return Array.from(nodes).map(([node]) => node) as S[]
  }

  updateReferences = ({ entityIdMap }: { entityIdMap: Map<string, string> }) => {
    const editor = this.#editor
    invariant(editor, 'Editor should be loaded')

    let refCount = 0
    let linkCount = 0

    const at = {
      anchor: Editor.start(editor, []),
      focus: Editor.end(editor, []),
    }
    const entityRefs = Editor.nodes(editor, {
      match: e => isCoreElement(e) && isEntityRef(e),
      mode: 'all',
      voids: true,
      at,
    })
    const entityLinks = Editor.nodes(editor, {
      match: e => isCoreElement(e) && isEntityLink(e),
      mode: 'all',
      voids: true,
      at,
    })

    // Replace ids in entity refs
    for (const entityRef of entityRefs) {
      const [node, path] = entityRef
      const entityId = node.entityId
      const newEntityId = entityIdMap.get(entityId)
      if (!newEntityId) {
        continue
      }
      Transforms.setNodes<CoreElement>(editor, { id: shortUuid(), entityId: newEntityId }, { at: path })
      refCount++
    }
    for (const entityLink of entityLinks) {
      const [node, path] = entityLink
      const entityId = node.ref?.id
      const newEntityId = entityIdMap.get(entityId)
      if (!newEntityId) {
        continue
      }
      Transforms.setNodes<CoreElement>(
        editor,
        {
          id: shortUuid(),
          ref: {
            ...node.ref,
            id: newEntityId,
          },
        },
        { at: path }
      )
      linkCount++
    }

    return { refCount, linkCount }
  }

  /** Replace all document ids with new ones. */
  replaceIds = () => {
    const editor = this.#editor
    invariant(editor, 'Editor should be loaded')

    const replaceMap = new Map<string, string>()

    const at = {
      anchor: Editor.start(editor, []),
      focus: Editor.end(editor, []),
    }

    const elements = Editor.nodes(editor, {
      match: e => isCoreElement(e),
      mode: 'all',
      voids: true,
      at,
    })
    for (const element of elements) {
      const [node, path] = element
      if (!node.id) {
        continue
      }
      invariant(node.id, 'Node should have an id')
      const newId = shortUuid()
      replaceMap.set(node.id, newId)
      Transforms.setNodes<CoreElement>(
        editor,
        {
          id: newId,
        },
        { at: path }
      )
    }

    return { replaceMap }
  }

  load = async () => {
    if (this.#element) {
      return this.#element
    }

    // If rawElement has children, we use that
    if (isCoreElement(this.rawEntity) && !isYDocStorage(this.rawEntity)) {
      this.#element = this.rawEntity
      this.#editor = createEditor()
      this.#editor.children = this.rawEntity.children
      return this.#element
    }

    // This is a YDoc-stored item - the yDoc lives in the doc store and must be a valid doc path
    invariant(
      isStoredObject(this.rawEntity) &&
        S3Doc.isDocPath({
          entityId: this.rawEntity.id,
          storage: this.rawEntity.storage,
          userId: this.rawEntity.userId,
        }),
      `Entity should be a stored object when it is not a CoreElement: ${JSON.stringify(this.rawEntity, null, 2)}` // TODO: remove this when we have a better way to handle this
    )

    // Retrieve the element
    const { storage } = this.rawEntity
    const yDoc = await docStore.getDoc({ storage, fallbackToLatest: true })

    this.#element = expandYDocV2(this.rawEntity, yDoc)

    this.#editor = createEditor()

    invariant(isValidSlateDocument(this.#element), 'Element should be a valid Slate document')
    this.#editor.children = this.#element.children

    return this.#element
  }
}

// Create entity node function

export const createEditableEntityNode = <T extends Persisted<Entity>>(el: T, bucketName: string) => {
  if (!(isCoreElement(el) || isYDocStorage(el.storage))) {
    logger.debug(
      `Entity ${el.id} is not a CoreElement or YDocStorage, so it's we don't create an EditableEntityNode for it`
    )
    return null
  }
  const node = new EditableEntityNode<T>(el, bucketName)
  return node
}
